import React, { memo, useRef } from 'react'
import Modal, { IProps as IModalProps, ModalSize } from 'marketing-site/src/components/common/Modal'
import { CTAButton, IProps as ICTAButtonProps } from 'marketing-site/src/library/elements/CTAButton'
import {
  ModalWithPersistedState,
  IProps as IModalWithPersistedStateProps,
} from 'marketing-site/src/library/elements/ModalWithPersistedState'
import * as Utils from 'marketing-site/src/library/utils'

import { IProps } from './index'
import useModalOpenState from 'marketing-site/lib/useModalOpenState'
import { AnalyticsEvent } from 'marketing-site/src/library/utils'

const ModalComponent = memo(({ ...props }: IModalProps | IModalWithPersistedStateProps) => {
  if ((props as IModalProps).isWistiaVideo) return <Modal {...(props as IModalProps)} />
  return <ModalWithPersistedState {...props} />
})

interface IButtonComponentProps {
  children: React.ReactNode
  label?: string
  ctaButton: (onClick: () => void) => ICTAButtonProps
  handleOnClick: () => void
  refButton?: React.RefObject<HTMLButtonElement>
}

const ButtonComponent = memo(
  ({ children, label, ctaButton, handleOnClick, refButton }: IButtonComponentProps) => {
    const ref = useRef<HTMLButtonElement>(null)
    const buttonRef = refButton || ref
    if (children)
      return (
        <button ref={buttonRef} onClick={handleOnClick}>
          {children}
        </button>
      )
    if (label) return <CTAButton refButton={buttonRef} {...ctaButton(handleOnClick)} />
    return null
  },
)

export const ModalButton: React.FC<IProps> = ({
  children,
  label,
  modalSize,
  backgroundColor,
  blurOverlay,
  autoOpen,
  preventClosing,
  ctaButton,
  renderModalContent,
  isWistiaVideo = false,
  eventContext,
  eventObjectName,
  refButton,
}) => {
  const [modalOpen, openModal, closeModal] = useModalOpenState({
    initialOpen: autoOpen,
  })

  const modalPreventClosing = preventClosing

  const modalSizeKey: keyof typeof ModalSize = modalSize
  const renderModalSize = ModalSize[modalSizeKey]

  const modalBlurOverlay: boolean | undefined = blurOverlay

  const ref = useRef<HTMLButtonElement>(null)
  const buttonRef = refButton || ref

  let modalBackgroundColor: Utils.Color | undefined

  if (backgroundColor) {
    modalBackgroundColor = Utils.getHexColorFromName(backgroundColor) as Utils.Color
  }

  const handleOnClick = () => {
    if (eventContext && eventObjectName) {
      const analyticsEvent = new AnalyticsEvent({
        action: 'clicked',
        object: eventObjectName,
        context: eventContext,
      })

      analyticsEvent.setPlaceFromPath(window.location.pathname)
      analyticsEvent.send()
    }

    openModal()
  }

  return (
    <>
      <ButtonComponent
        refButton={buttonRef}
        label={label}
        handleOnClick={handleOnClick}
        ctaButton={ctaButton}
      >
        {children}
      </ButtonComponent>
      <ModalComponent
        isOpen={modalOpen}
        onRequestClose={!modalPreventClosing ? closeModal : () => {}}
        size={renderModalSize}
        backgroundColor={modalBackgroundColor}
        showCloseIcon={!modalPreventClosing}
        hasBlurredOverlay={modalBlurOverlay}
        isWistiaVideo={isWistiaVideo}
      >
        {renderModalContent()}
      </ModalComponent>
    </>
  )
}
